import { yupResolver } from "@hookform/resolvers/yup";
import PhoneInput, { CountryData } from "react-phone-input-2";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import httpClient from "services/network/httpClient";
import Input from "components/Input";
import PillButton from "components/button/Pills";
import { showToast } from "components/Toaster";
import "react-phone-input-2/lib/style.css";
import React from "react";
import { ValidationSchemas, } from "utility/validationUtils";

export interface FarmersProps {
  farmerName: string;
  village: string;
  houseNumber: string;
  pinCode: string;
  state: string;
  district: string;
  tehsilMandal: string;
  farmerContactNumber: string;
}

const validationSchema = yup.object().shape({
  farmerName: ValidationSchemas.string('Farmer Name'),
  village: ValidationSchemas.string('Village'),
  houseNumber: ValidationSchemas.string('House Number'),
  pinCode: ValidationSchemas.postalCode(),
  state: ValidationSchemas.string('State'),
  district: ValidationSchemas.string('District'),
  tehsilMandal: ValidationSchemas.string('Tehsil/Mandal'),
  farmerContactNumber: yup
    .string()
    .required("Farmer contact number is required")
    .test(
      "is-valid-number",
      "Enter a valid 10-digit contact number",
      (value) => {
        if (!value) return false;
        const digits = value.replace(/\D/g, "");
        const cleaned = digits.startsWith("91") ? digits.slice(2) : digits;
        return cleaned.length === 10;
      }
    ),
  // farmerContactNumber removed from schema, handled separately
});

interface AddProps {
  onClose: () => void;
  getData: () => void;
}

const AddFarmers = ({ onClose, getData }: AddProps) => {
  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });
  const [farmerContactNumber, setFarmerContactNumber] = React.useState("");
  const [dialCode, setDialCode] = React.useState("");


  const submit = (data: any) => {

    let nationalNumber = farmerContactNumber;
    // Remove leading + if present
    if (nationalNumber.startsWith("+")) {
      nationalNumber = nationalNumber.slice(1);
    }
    // Remove dialCode only if it is at the start
    if (dialCode && nationalNumber.startsWith(dialCode)) {
      nationalNumber = nationalNumber.slice(dialCode.length);
    }
    // Remove any leading zeros
    nationalNumber = nationalNumber.replace(/^0+/, "");
    const payload = {
      ...data,
      farmerContactNumber: `+${dialCode}${nationalNumber}`,
      // countryCode: dialCode,
    };
    httpClient
      .post("farmers/create", payload)
      .then((response: any) => {
        console.log("inside Add Farmers response ", response);
        if (response.success === true) {
          showToast("Farmer Added success", "success");
        } else {
          showToast(`${response.error.message}`, "error");
        }
      })
      .catch((error) => {
        console.log(error);
        showToast("something went wrong", "error");
      })
      .finally(() => {
        onClose();
        getData();
      });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submit)}>
        <div className="md:grid md:grid-cols-3 gap-10">
          <div className="w-full md:col-span-1">
            <Input
              name={"farmerName"}
              label={"Farmer Name"}
              placeholder="Enter Farmer Name"
              required={true}
              onlyLetters
            />
          </div>

          <div className="w-full col-span-1">
            <Input
              name={"houseNumber"}
              label={"House Number"}
              placeholder="Enter Farmer House Number"
              required={true}
            />
          </div>

          <div className="w-full col-span-1 ">
            <label
              htmlFor="farmerContactNumber"
              className="mb-1 font-medium text-gray-900 dark:text-gray-200"
            >
              Farmer Contact Number <span className="text-red-500">*</span>
            </label>
            <PhoneInput
              country={"in"}
              value={farmerContactNumber}
              onChange={(value, country) => {
                setFarmerContactNumber(value);
                setDialCode((country as CountryData).dialCode ?? "");

                methods.setValue("farmerContactNumber", value, {
                  shouldValidate: true,
                });
              }}
              //phone input field background and dropdown background color
              inputClass="!w-full !bg-white !text-[#374151] dark:!bg-gray-800 dark:!text-white !border-none"
              buttonClass="!bg-white dark:!bg-gray-700"
              dropdownClass="
  !bg-white !text-[#374151] 
  dark:!bg-gray-800 dark:!text-white

  [&>.country:hover]:!bg-gray-100 
  [&>.country:hover]:!text-black
  dark:[&>.country:hover]:!bg-gray-700 
  dark:[&>.country:hover]:!text-white

  [&>.country.highlight]:!bg-gray-200 
  [&>.country.highlight]:!text-black
  dark:[&>.country.highlight]:!bg-gray-700 
  dark:[&>.country.highlight]:!text-white"//text color when cursor over the dropdown
              inputProps={{
                id: "farmerContactNumber",
                name: "farmerContactNumber",
                required: true,
                autoFocus: false,
              }}
              searchClass="!text-black dark:!text-black !bg-white dark:!bg-gray-700" //seach field text color
              enableSearch
              inputStyle={{
                width: "100%",
                height: "38px",
                borderRadius: "0.5rem",
                border: "none",
                fontSize: "14px",
              }}
              buttonStyle={{
                border: "none",
                background: "none",
                borderRadius: "0.5rem 0 0 0.5rem",
              }}
              containerStyle={{
                width: "100%",
                borderRadius: "0.5rem",
                border: "1px solid #d1d5db",   // <- light mode border visible
              }}
            />
            {methods.formState.errors.farmerContactNumber && (
              <span className="text-red-600 text-sm font-medium mt-1 block">
                {methods.formState.errors.farmerContactNumber.message as string}
              </span>
            )}
          </div>

          <div className="w-full col-span-1">
            <Input
              name={"village"}
              label={"Village"}
              placeholder="Enter Farmer Village"
              required={true}
              onlyLetters
            />
          </div>

          <div className="w-full col-span-1">
            <Input
              name={"district"}
              label={"District"}
              placeholder="Enter Farmer District"
              required={true}
              onlyLetters
            />
          </div>

          <div className="w-full col-span-1">
            <Input
              name={"state"}
              label={"State"}
              placeholder="Enter Farmer State"
              required={true}
              onlyLetters
            />
          </div>

          <div className="w-full col-span-1 ">
            <Input
              name={"tehsilMandal"}
              label={"Tehsil Mandal"}
              placeholder="Enter Farmer Tehsil Mandal"
              required={true}
              onlyLetters
            />
          </div>

          <div className="w-full col-span-1">
            <Input
              name={"pinCode"}
              label={"PIN Code"}
              placeholder="Enter 6-digit PIN Code"
              required={true}
              maxLength={6}
              onlyDigits
              onChange={(e) => {
                // Allow only digits and limit to 6 characters
                e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6);
              }}
              helperText="Must be exactly 6 digits"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-row items-center justify-end gap-10">
          <PillButton type="submit" variant="secondary">
            {" "}
            Submit{" "}
          </PillButton>
          <PillButton type="reset" variant="primary" onClick={onClose}>
            {" "}
            Cancel{" "}
          </PillButton>
        </div>
      </form>
    </FormProvider>
  );
};
export default AddFarmers;
